import React, { useState, useEffect, useRef } from "react"
import { Col, Row, Form, Input } from "reactstrap"
import { useFormik } from "formik"
import toast from "react-hot-toast"
import moment from "moment"
import ReactDatePicker from "react-datepicker"
import ReactQuill from "react-quill"

import { Modal } from "../../../components/Modal"
import { TextInput } from "../../../components/TextInput"
import { Button } from "../../../components/Button"
import FormikErrorText from "../../../components/FormikErrorText/FormikErrorText"
import ImageGallery from "../CommonUI/ImageGallery/ImageGallery"
import {
   GetTrailersDetails,
   CreateTrailer,
   UpdateTrailer,
   GeTrailersTypesListing,
} from "../../../api/api.service"
import {
   initializeMediaUtil,
   uploadOnS3,
   finalizeMediaUtil,
} from "../../../utils/mediaUtils"
import { FIELDS_NAME, initialValues, validationSchema } from "./FormConfig"
import ActiveInactiveSwitch from "../CommonUI/ActiveInactiveSwitch/ActiveInactiveSwitch"
import { convertToSelectOptions } from "utils/commonUtils"
import {
   TrailerPublishStatus,
   TrailerSize,
   TrailerStatus,
   TrailerType,
} from "../../../constants"
import TrailerModels from "constants/TrailerModels"
import FormSkeleton from "components/FormSkeleton/FormSkeleton"
import "react-datepicker/dist/react-datepicker.css"
import { AutoComplete } from "components/AutoComplete"
import useAsyncSelect from "hooks/useAsyncSelect"
import autoCompleteStyles from "components/AutoComplete/autoCompleteStyles"

const TITLES = {
   MODAL_ADD_TITLE: "Add new trailer",
   MODAL_UPDATE_TITLE: "Update trailer",
   MODAL_DETAILS_TITLE: "Trailer details",
   ADD_BTN_TEXT: "Save",
   UPDATE_BTN_TEXT: "Update",
   DETAILS_BTN_TEXT: "Done",
}

const handleStatusConversion = status => {
   return status == TrailerStatus.Active ? true : false
}

const handleUpdateExecution = async (id, data) => {
   let res = await UpdateTrailer(id, data)
   toast.success("Trailer has been updated successfully !")
}

const getPublishStatus = selectedDate => {
   if (moment(selectedDate).isSame(moment().format("YYYY-MM-DD"), "day")) {
      return TrailerPublishStatus.Published
   } else {
      return TrailerPublishStatus.UnPublished
   }
}

const ManageTrailer = ({
   isDetailView = false,
   isOpen = false,
   onToggleModal = () => {},
   onSuccess = () => {},
   id = "",
}) => {
   const imagesRef = useRef(null)
   const modelImageRef = useRef(null)

   const [isLoading, setIsLoading] = useState(false)
   const [status, setStatus] = useState(true)
   const [previousImages, setPreviousImages] = useState([])
   const [modelPreviousImage, setModelPreviousImage] = useState([])
   const [isFetching, setIsFetching] = useState(false)

   const formik = useFormik({
      enableReinitialze: true,
      initialValues: {
         ...initialValues,
      },
      validationSchema: validationSchema,
      onSubmit: values => {
         handleSubmit(values)
      },
   })

   const {
      setSelectedOption: setSelectedTrailerType,
      selectEntity: trailerTypeOptions,
      setSelectEntity: setTrailerTypeOptions,
      handleSearchOnOptions: handleSearchTrailerType,
      selectedOption: selectedTrailerType,
      handleSelectAOption: handleSelectATrailerType,
   } = useAsyncSelect({
      apiFunc: GeTrailersTypesListing,
      labelKey: "trailerType",
      valueKey: "id",
      params: {},
      onOptionSelect: option => {
         formik.setFieldValue([FIELDS_NAME.TRAILER_TYPE], option)
      },
   })

   useEffect(() => {
      handleFetchDetails()
      return () => {
         setIsLoading(false)
      }
   }, [id])

   const handleSubmit = values => {
      const formattedDate = moment(values[FIELDS_NAME.PUBLISH_DATE])
         .set({ hour: 19, minute: 0, second: 0, millisecond: 0 })
         .toISOString()
      const bodyData = {
         model: [values[FIELDS_NAME.MODEL_NAME].value],
         modelDescription: values[FIELDS_NAME.MODEL_DESCRIPTION],
         price: values[FIELDS_NAME.PRICE].toString(),
         configuratorTrailerSizeType: values[FIELDS_NAME.SIZE],
         publishingDate: formattedDate,
         publishingStatus: getPublishStatus(formattedDate),
         trailerYear: parseInt(moment(values[FIELDS_NAME.YEAR]).format("YYYY")),
         trailerTypeId: values[FIELDS_NAME.TRAILER_TYPE].value,
         status: values[FIELDS_NAME.STATUS],
         description: values[FIELDS_NAME.DESCRIPTION],
      }

      if (id) {
         handleUpdate(bodyData)
      } else {
         handleAdd(bodyData)
      }
   }

   const uploadedMediaIDs = async images => {
      if (images) {
         try {
            let res = await handleUploadImages(images)
            return res?.length && res.map(item => item.id)
         } finally {
         }
      }
   }

   const handleUpdate = async data => {
      const payload = {
         ...data,
      }

      let userUploadedImages = imagesRef.current.getAllImages()
      let modelUploadedImages = modelImageRef.current.getAllImages()

      if (!userUploadedImages.length && !previousImages.length) {
         return toast.error("Upload trailer images !")
      }

      if (!modelUploadedImages.length && !modelPreviousImage.length) {
         return toast.error("Upload model image !")
      }

      try {
         setIsLoading(true)

         if (userUploadedImages.length) {
            const uploadedImages = await uploadedMediaIDs(userUploadedImages)
            const apiFormatForImages = uploadedImages?.map(imageId => ({
               mediaId: imageId,
            }))

            payload.images = [
               ...(apiFormatForImages?.length && apiFormatForImages),
               ...(previousImages?.length
                  ? previousImages.map(image => ({
                       mediaId: image.media.id,
                    }))
                  : []),
            ]
         } else {
            payload.images = [
               ...(previousImages?.length &&
                  previousImages.map(image => ({
                     mediaId: image.mediaId,
                  }))),
            ]
         }

         if (modelUploadedImages.length) {
            const uploadedModelImages = await uploadedMediaIDs(
               modelUploadedImages
            )
            const apiFormatForModelImages = uploadedModelImages?.map(
               imageId => ({
                  mediaId: imageId,
               })
            )

            payload.modelImageMediaId =
               apiFormatForModelImages?.length &&
               apiFormatForModelImages[0].mediaId
         } else {
            payload.modelImageMediaId =
               modelPreviousImage?.length && modelPreviousImage[0]?.media?.id
         }

         await handleUpdateExecution(id, payload)
         handleModalClose()
         onSuccess()
      } finally {
         setIsLoading(false)
      }
   }
   const handleAdd = async data => {
      let userUploadedImages = imagesRef.current.getAllImages()
      let modelUploadedImages = modelImageRef.current.getAllImages()

      if (!userUploadedImages.length) {
         toast.error("Upload trailer images !")
         return
      }

      if (!modelUploadedImages.length) {
         toast.error("Upload model image !")
         return
      }

      try {
         setIsLoading(true)
         let ids = await uploadedMediaIDs(userUploadedImages)
         let modelIds = await uploadedMediaIDs(modelUploadedImages)
         let apiFormatForImages = ids.map(imageId => ({
            mediaId: imageId,
         }))
         let apiFormatForModelImages = modelIds.map(imageId => ({
            mediaId: imageId,
         }))
         data.modelImageMediaId =
            apiFormatForModelImages.length && apiFormatForModelImages[0].mediaId
         data.images = [...(apiFormatForImages.length && apiFormatForImages)]

         let res = await CreateTrailer(data)
         toast.success("Trailer has been added successfully !")
         onSuccess()
         handleModalClose()
      } catch (error) {
      } finally {
         setIsLoading(false)
      }
   }

   const handleFetchDetails = async () => {
      if (!id) return

      try {
         setIsFetching(true)
         let res = await GetTrailersDetails(id)

         setSelectedTrailerType({
            label: res?.trailerType?.trailerType,
            value: res?.trailerType?.id,
         })

         formik.setValues({
            [FIELDS_NAME.MODEL_NAME]: {
               label: res?.model[0],
               value: res?.model[0],
            },
            [FIELDS_NAME.MODEL_DESCRIPTION]: res?.modelDescription,
            [FIELDS_NAME.PRICE]: res?.price,
            [FIELDS_NAME.PUBLISH_DATE]: res?.publishingDate
               ? moment(res?.publishingDate).format("YYYY-MM-DD")
               : null,
            [FIELDS_NAME.PUBLISH_STATUS]: res?.publishingStatus,
            [FIELDS_NAME.DESCRIPTION]: res?.description,

            [FIELDS_NAME.SIZE]: res?.configuratorTrailerSizeType,
            [FIELDS_NAME.STATUS]: res?.status,
            [FIELDS_NAME.TRAILER_TYPE]: {
               label: res?.trailerTypeId,
               value: res?.trailerTypeId,
            },
            [FIELDS_NAME.YEAR]: moment(res?.trailerYear, "YYYY").toISOString(),
         })
         setStatus(handleStatusConversion(res?.status))
         setPreviousImages(res?.images)
         res?.modelImage && setModelPreviousImage([res?.modelImage])
      } finally {
         setIsFetching(false)
      }
   }

   const handleUploadImages = async images => {
      let apiCallIns
      let uploadedIds = []

      apiCallIns = images.map(item => {
         return handleMediaUpload(item)
      })

      try {
         setIsLoading(true)
         uploadedIds = (await Promise.allSettled(apiCallIns))
            .filter(item => item.status === "fulfilled")
            .map(id => id.value)
      } finally {
         setIsLoading(false)
      }

      return uploadedIds
   }

   const handleMediaUpload = async file => {
      return initializeMediaUtil(file).then(async res => {
         const credentials = res
         await uploadOnS3(file, credentials, handleImageUploadProgress)
         return await finalizeMediaUtil(credentials?.mediaId)
      })
   }

   const handleImageUploadProgress = progress => {}

   const handleModalClose = () => {
      setTimeout(() => {
         setStatus(true)
         formik.resetForm()
         setSelectedTrailerType(null)
         setPreviousImages([])
         setModelPreviousImage([])
         onToggleModal()
      }, 20)
   }

   const handleSetStatus = evt => {
      let { name } = evt.target
      let alteredStatus = !status
      setStatus(alteredStatus)
      formik.setFieldValue(
         name,
         alteredStatus ? TrailerStatus.Active : TrailerStatus.Inactive
      )
   }

   const handleCheckboxChange = (e, fieldName) => {
      const { value } = e.target
      formik.setFieldValue(fieldName, [value])
   }

   const { price, publishingDate, year } = formik.values

   let formattedYear
   if (year instanceof Date) {
   } else if (typeof year === "string") {
      const parsedDate = new Date(year)
      formattedYear = isNaN(parsedDate)
         ? "Invalid date"
         : parsedDate.getFullYear()
   } else {
      formattedYear = "Invalid date"
   }

   return (
      <Modal
         isOpen={isOpen}
         handleModalToggling={handleModalClose}
         bodyClassName=""
         customButton={true}
         hideModalHeaderSeparator={true}
         headerClasses="header-container"
         sizeClasses="8"
         backdrop="static"
         modalTitle={
            isDetailView
               ? TITLES.MODAL_DETAILS_TITLE
               : id
               ? TITLES.MODAL_UPDATE_TITLE
               : TITLES.MODAL_ADD_TITLE
         }
         scrollable
      >
         {isFetching ? (
            <FormSkeleton />
         ) : (
            <div style={{ pointerEvents: isLoading ? "none" : "auto" }}>
               <Row>
                  <Col xs={12}>
                     <Form onSubmit={formik.handleSubmit}>
                        <Row className="mb-3 align-items-center">
                           <label className=" col-md-3 col-form-label clr-theme-primary">
                              Trailer Type
                           </label>

                           <div className="col-md-9">
                              {isDetailView ? (
                                 <small>{selectedTrailerType?.label}</small>
                              ) : (
                                 <>
                                    <AutoComplete
                                       handleBlur={() =>
                                          setTrailerTypeOptions(prev => {
                                             return {
                                                ...prev,
                                                page: 1,
                                                Q: "",
                                             }
                                          })
                                       }
                                       onChange={handleSelectATrailerType}
                                       onInputChange={handleSearchTrailerType}
                                       placeholder="Search trailer types"
                                       classNamePrefix="add-new-req"
                                       customStyles={
                                          autoCompleteStyles.STYLE_FOUR
                                       }
                                       options={trailerTypeOptions?.data}
                                       value={selectedTrailerType}
                                       isLoading={trailerTypeOptions?.fetching}
                                       name={FIELDS_NAME.TRAILER_TYPE}
                                    />
                                    <FormikErrorText
                                       formikInstance={formik}
                                       fieldName={FIELDS_NAME.TRAILER_TYPE}
                                    />
                                 </>
                              )}
                           </div>
                        </Row>

                        <Row className="mt-4 mb-3 align-items-center">
                           <label className=" col-md-3 col-form-label clr-theme-primary">
                              Price ($)
                           </label>
                           <div className="col-md-9">
                              {isDetailView ? (
                                 <small>{price}</small>
                              ) : (
                                 <>
                                    <TextInput
                                       type="number"
                                       min={0}
                                       step="0.01"
                                       inputClass={
                                          isDetailView && "input-readonly"
                                       }
                                       placeholder="Enter trailer price"
                                       name={FIELDS_NAME.PRICE}
                                       {...formik.getFieldProps(
                                          FIELDS_NAME.PRICE
                                       )}
                                    />

                                    <FormikErrorText
                                       formikInstance={formik}
                                       fieldName={FIELDS_NAME.PRICE}
                                    />
                                 </>
                              )}
                           </div>
                        </Row>

                        <Row className="mb-3 align-items-center">
                           <label className=" col-md-3 col-form-label clr-theme-primary">
                              Trailer Description
                           </label>
                           <div className="col-md-9">
                              {!isDetailView && (
                                 <ReactQuill
                                    className="quill-description-write"
                                    theme="snow"
                                    placeholder="Enter item description"
                                    value={
                                       formik.values[FIELDS_NAME.DESCRIPTION]
                                    }
                                    onChange={value =>
                                       formik.setFieldValue(
                                          FIELDS_NAME.DESCRIPTION,
                                          value
                                       )
                                    }
                                 />
                              )}

                              {isDetailView && (
                                 <div
                                    className="quill-description-readonly"
                                    dangerouslySetInnerHTML={{
                                       __html:
                                          formik.values[
                                             FIELDS_NAME.DESCRIPTION
                                          ],
                                    }}
                                 />
                              )}

                              <FormikErrorText
                                 formikInstance={formik}
                                 fieldName={FIELDS_NAME.DESCRIPTION}
                              />
                           </div>
                        </Row>

                        <Row className="mt-4 mb-3 align-items-center">
                           <label className=" col-md-3 col-form-label clr-theme-primary">
                              Publish Date
                           </label>
                           <div className="col-md-9">
                              {isDetailView ? (
                                 <small>
                                    {publishingDate
                                       ? publishingDate instanceof Date
                                          ? publishingDate.toLocaleDateString()
                                          : publishingDate
                                       : "-"}
                                 </small>
                              ) : (
                                 <>
                                    <ReactDatePicker
                                       autoComplete="off"
                                       withPortal
                                       dateFormat={"yyyy-MM-dd"}
                                       showTimeInput={false}
                                       selected={
                                          formik.values[
                                             FIELDS_NAME.PUBLISH_DATE
                                          ]
                                             ? new Date(
                                                  formik.values[
                                                     FIELDS_NAME.PUBLISH_DATE
                                                  ]
                                               )
                                             : ""
                                       }
                                       placeholderText="Select publish date"
                                       minDate={new Date()}
                                       name={FIELDS_NAME.PUBLISH_DATE}
                                       onChange={date =>
                                          formik.setFieldValue(
                                             FIELDS_NAME.PUBLISH_DATE,
                                             date
                                          )
                                       }
                                       className={`custom-date-picker ${
                                          isDetailView && "input-readonly"
                                       }`}
                                    />

                                    <FormikErrorText
                                       formikInstance={formik}
                                       fieldName={FIELDS_NAME.PUBLISH_DATE}
                                    />
                                 </>
                              )}
                           </div>
                        </Row>

                        <Row className="mt-4 mb-3 align-items-center">
                           <label className=" col-md-3 col-form-label clr-theme-primary">
                              Year
                           </label>
                           <div className="col-md-9">
                              {isDetailView ? (
                                 <small>{formattedYear}</small>
                              ) : (
                                 <>
                                    <ReactDatePicker
                                       showYearPicker
                                       withPortal
                                       dateFormat={"yyyy"}
                                       selected={
                                          new Date(
                                             formik.values[FIELDS_NAME.YEAR]
                                          )
                                       }
                                       placeholderText="Select year"
                                       name={FIELDS_NAME.YEAR}
                                       onChange={year => {
                                          formik.setFieldValue(
                                             FIELDS_NAME.YEAR,
                                             year
                                          )
                                       }}
                                       className={`custom-date-picker ${
                                          isDetailView && "input-readonly"
                                       }`}
                                       showTimeInput={false}
                                    />

                                    <FormikErrorText
                                       formikInstance={formik}
                                       fieldName={FIELDS_NAME.YEAR}
                                    />
                                 </>
                              )}
                           </div>
                        </Row>

                        <Row className="mt-4 mb-3 align-items-center">
                           <label className="col-md-3 col-form-label clr-theme-primary">
                              Model
                           </label>
                           <Col md={9}>
                              <fieldset className="custom-fieldset">
                                 <legend className="custom-legend">
                                    Model Information
                                 </legend>

                                 <Row>
                                    <Col sm={12}>
                                       <small
                                          style={{
                                             fontWeight: 500,
                                             fontSize: "0.7rem",
                                          }}
                                       >
                                          Model Name
                                       </small>
                                       {isDetailView ? (
                                          <small className="d-block">
                                             {
                                                formik.values[
                                                   FIELDS_NAME.MODEL_NAME
                                                ]?.label
                                             }
                                          </small>
                                       ) : (
                                          <>
                                             <AutoComplete
                                                name={FIELDS_NAME.MODEL_NAME}
                                                value={
                                                   formik.values[
                                                      FIELDS_NAME.MODEL_NAME
                                                   ]
                                                }
                                                placeholder="Select model"
                                                isSearchable={false}
                                                classNamePrefix="add-new-req"
                                                customStyles={
                                                   autoCompleteStyles.STYLE_FOUR
                                                }
                                                options={convertToSelectOptions(
                                                   TrailerModels
                                                )}
                                                onChange={val =>
                                                   formik.setFieldValue(
                                                      FIELDS_NAME.MODEL_NAME,
                                                      val
                                                   )
                                                }
                                             />
                                             <FormikErrorText
                                                formikInstance={formik}
                                                fieldName={
                                                   FIELDS_NAME.MODEL_NAME
                                                }
                                             />
                                          </>
                                       )}
                                    </Col>

                                    <Col sm={12} className="my-2">
                                       {isDetailView &&
                                       FIELDS_NAME.MODEL_DESCRIPTION ? (
                                          <></>
                                       ) : (
                                          <small
                                             style={{
                                                fontWeight: 500,
                                                fontSize: "0.7rem",
                                             }}
                                          >
                                             Model Description
                                          </small>
                                       )}
                                       {!isDetailView ? (
                                          <ReactQuill
                                             className="quill-description-write"
                                             theme="snow"
                                             placeholder="Enter model description"
                                             value={
                                                formik.values[
                                                   FIELDS_NAME.MODEL_DESCRIPTION
                                                ]
                                             }
                                             onChange={value =>
                                                formik.setFieldValue(
                                                   FIELDS_NAME.MODEL_DESCRIPTION,
                                                   value
                                                )
                                             }
                                          />
                                       ) : (
                                          <div
                                             className="quill-description-readonly"
                                             dangerouslySetInnerHTML={{
                                                __html:
                                                   formik.values[
                                                      FIELDS_NAME
                                                         .MODEL_DESCRIPTION
                                                   ],
                                             }}
                                          />
                                       )}

                                       <FormikErrorText
                                          formikInstance={formik}
                                          fieldName={
                                             FIELDS_NAME.MODEL_DESCRIPTION
                                          }
                                       />
                                    </Col>

                                    {isDetailView &&
                                    modelPreviousImage.length == 0 ? (
                                       <></>
                                    ) : (
                                       <Col sm={12}>
                                          <small
                                             style={{
                                                fontWeight: 500,
                                                fontSize: "0.7rem",
                                             }}
                                          >
                                             Model Image
                                          </small>

                                          <ImageGallery
                                             MAX_IMAGES={1}
                                             isMulti={false}
                                             ref={modelImageRef}
                                             previousImages={modelPreviousImage}
                                             removePreviousImages={
                                                setModelPreviousImage
                                             }
                                             previewOnly={isDetailView}
                                             isDetailView={isDetailView}
                                          />
                                       </Col>
                                    )}
                                 </Row>
                              </fieldset>
                           </Col>
                        </Row>

                        <Row className="mt-4 mb-3 align-items-center">
                           <label className="col-md-3 col-form-label clr-theme-primary">
                              Size
                           </label>
                           <div className="col-md-9">
                              <div className="d-flex">
                                 {Object.keys(TrailerSize).map(sizeKey => (
                                    <div
                                       key={sizeKey}
                                       className="d-flex align-items-center"
                                    >
                                       <Input
                                          type="checkbox"
                                          name={FIELDS_NAME.SIZE}
                                          value={TrailerSize[sizeKey]}
                                          checked={
                                             formik.values[
                                                FIELDS_NAME.SIZE
                                             ][0] === TrailerSize[sizeKey]
                                          }
                                          onClick={e =>
                                             handleCheckboxChange(
                                                e,
                                                FIELDS_NAME.SIZE
                                             )
                                          }
                                          className={
                                             isDetailView &&
                                             "input-readonly-checkboxes"
                                          }
                                       />
                                       <span className="mx-2">
                                          {TrailerSize.getDisplayTextKey(
                                             TrailerSize[sizeKey]
                                          )}
                                       </span>
                                    </div>
                                 ))}
                              </div>

                              <FormikErrorText
                                 formikInstance={formik}
                                 fieldName={FIELDS_NAME.SIZE}
                              />
                           </div>
                        </Row>

                        <Row className="mt-4 mb-3 align-items-start">
                           <label className=" col-md-3  clr-theme-primary">
                              Upload Images
                           </label>
                           <div className="col-md-9">
                              <ImageGallery
                                 ref={imagesRef}
                                 previousImages={previousImages}
                                 removePreviousImages={setPreviousImages}
                                 previewOnly={isDetailView}
                                 isDetailView={isDetailView}
                              />
                           </div>
                        </Row>

                        <Row className="mb-3 align-items-center">
                           <label className=" col-md-3 col-form-label clr-theme-primary">
                              Status
                           </label>
                           <div className="col-md-9">
                              <ActiveInactiveSwitch
                                 isChecked={status}
                                 isDisable={isDetailView}
                                 name={FIELDS_NAME.STATUS}
                                 onChange={handleSetStatus}
                              />
                              <FormikErrorText
                                 formikInstance={formik}
                                 fieldName={FIELDS_NAME.STATUS}
                              />
                           </div>
                        </Row>

                        <div className="d-flex justify-content-center mt-3 mt-2">
                           <Button
                              isLoading={isLoading}
                              type={isDetailView ? "button" : "submit"}
                              title={
                                 isDetailView
                                    ? TITLES.DETAILS_BTN_TEXT
                                    : id
                                    ? TITLES.UPDATE_BTN_TEXT
                                    : TITLES.ADD_BTN_TEXT
                              }
                              className="header-button"
                              onClick={isDetailView && handleModalClose}
                           />
                        </div>
                     </Form>
                  </Col>
               </Row>
            </div>
         )}
      </Modal>
   )
}

export default React.memo(ManageTrailer)
